import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../translations/translations';
import AnimatedSection from './AnimatedSection';
import { HandDrawnDivider } from './DecorativeElements';

// Component to handle vendor logo display with fallback
const VendorLogo = ({ vendor, language }) => {
  const [imageError, setImageError] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  // Check if image exists
  const checkImageExists = (url) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve(true);
      img.onerror = () => resolve(false);
      img.src = url;
    });
  };

  React.useEffect(() => {
    checkImageExists(vendor.logo).then((exists) => {
      if (!exists) {
        setImageError(true);
      }
    });
  }, [vendor.logo]);

  if (imageError) {
    // Show text-based fallback
    return (
      <div className="text-center w-full">
        <div className="inline-block bg-rustic-cream px-6 py-4 rounded-lg border-2 border-latte">
          <h3 className="text-xl md:text-2xl font-serif text-deep-brown mb-1">
            {vendor.name}
          </h3>
          <p className="text-sm text-terracotta font-body">
            {language === 'en' ? vendor.category : vendor.categoryEs}
          </p>
        </div>
        <p className="text-xs text-deep-brown/60 mt-2 italic">
          {language === 'en' ? 'Logo coming soon' : 'Logo próximamente'}
        </p>
      </div>
    );
  }

  // Encode the logo path to handle spaces and special characters
  const encodedLogo = encodeURI(vendor.logo);

  return (
    <>
      <img
        src={encodedLogo}
        alt={`${vendor.name} logo`}
        className={`max-h-24 w-auto object-contain filter grayscale group-hover:grayscale-0 transition-all duration-300 ${
          imageLoaded ? 'opacity-100' : 'opacity-0'
        }`}
        onLoad={() => setImageLoaded(true)}
        onError={() => setImageError(true)}
      />
      {!imageLoaded && !imageError && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="animate-pulse text-deep-brown/40 text-sm">Loading...</div>
        </div>
      )}
    </>
  );
};

const Vendors = () => {
  const { language } = useLanguage();
  const t = translations[language];

  const vendors = [
    {
      name: "Jovanny's CAKERY",
      category: "Cakery & Desserts",
      categoryEs: "Pastelería y Postres",
      description: "Elegant custom cakes and desserts",
      descriptionEs: "Pasteles y postres elegantes personalizados",
      logo: "/img/vendors/jovannys-new-logo.jpg",
      website: "https://www.instagram.com/jovannyscakery?igsh=d3MwYWdwODU2emMz",
      isInstagram: true,
    },
    {
      name: "We Are PRODUCTION",
      category: "Photography & Video",
      categoryEs: "Fotografía y Video",
      description: "Professional photography and video services",
      descriptionEs: "Servicios profesionales de fotografía y video",
      logo: "/img/vendors/logo We Are production.png",
      website: "https://www.instagram.com/weareproductionstudio?igsh=MTVneW53aDloaDZwbg==",
      isInstagram: true,
    },
    {
      name: "Elegance Events Dallas",
      category: "Wedding Planning & Coordination",
      categoryEs: "Planificación y Coordinación de Bodas",
      description: "Dallas wedding planners and day-of coordinators",
      descriptionEs: "Planificadores de bodas y coordinadores del día del evento en Dallas",
      logo: "/img/vendors/elegance-events-logo.png",
      website: "https://eleganceeventsdallas.com/",
      isInstagram: false,
    },
    {
      name: "DJ TONY EVENTOS VIP",
      category: "DJ & Entertainment",
      categoryEs: "DJ y Entretenimiento",
      description: "Professional DJ services for your special events",
      descriptionEs: "Servicios profesionales de DJ para tus eventos especiales",
      logo: "/img/vendors/dj-tony-eventos-vip-logo.png",
      website: "https://www.instagram.com/dj_tony11?igsh=dzUxcGU3MDlkM24=",
      isInstagram: true,
    },
  ];

  return (
    <section id="vendors" className="section-padding bg-rustic-cream texture-overlay">
      <div className="container mx-auto">
        <AnimatedSection animation="fadeInUp">
          <div className="text-center mb-16">
            <h2 className="section-heading">
              {language === 'en' ? 'Preferred Vendors' : 'Proveedores Preferidos'}
            </h2>
            <p className="text-lg md:text-xl font-serif text-terracotta italic mb-4">
              {language === 'en' 
                ? 'Trusted partners for your special day' 
                : 'Socios de confianza para tu día especial'}
            </p>
            <HandDrawnDivider />
          </div>
        </AnimatedSection>

        <div className="grid md:grid-cols-2 lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
          {vendors.map((vendor, index) => (
            <AnimatedSection 
              key={vendor.name} 
              animation={index % 2 === 0 ? "slideInLeft" : "slideInRight"} 
              delay={index * 100}
            >
              <div className="card-rustic group">
                <div className="p-8">
                  {/* Logo Container */}
                  <div className="flex justify-center items-center mb-6 min-h-[120px] bg-white rounded-lg p-6 shadow-inner relative">
                    <VendorLogo 
                      vendor={vendor} 
                      language={language}
                    />
                  </div>

                  {/* Vendor Info */}
                  <div className="text-center">
                    <h3 className="text-2xl font-serif text-deep-brown mb-2 group-hover:text-terracotta transition-colors">
                      {vendor.name}
                    </h3>
                    <p className="text-terracotta font-body mb-3">
                      {language === 'en' ? vendor.category : vendor.categoryEs}
                    </p>
                    <p className="text-deep-brown/80 font-body text-sm mb-4">
                      {language === 'en' ? vendor.description : vendor.descriptionEs}
                    </p>
                    
                    {vendor.website && vendor.website !== '#' && (
                      <a
                        href={vendor.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-block text-terracotta hover:text-burnt-orange font-body text-sm underline transition-colors"
                      >
                        {vendor.isInstagram 
                          ? (language === 'en' ? 'Follow on Instagram' : 'Seguir en Instagram')
                          : (language === 'en' ? 'Visit Website' : 'Visitar Sitio Web')
                        } →
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </AnimatedSection>
          ))}
        </div>

        {/* Call to Action */}
        <AnimatedSection animation="fadeInUp" delay={300}>
          <div className="text-center mt-12">
            <p className="text-lg font-body text-deep-brown mb-4">
              {language === 'en' 
                ? 'Interested in becoming a preferred vendor?' 
                : '¿Interesado en convertirse en un proveedor preferido?'}
            </p>
            <a
              href="#contact"
              className="btn-secondary inline-block"
            >
              {language === 'en' ? 'Contact Us' : 'Contáctanos'}
            </a>
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
};

export default Vendors;
